import { TooltipProvider } from "@/components/ui/tooltip";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { HelmetProvider } from "react-helmet-async";
import { useEffect } from "react";
import { InstallPrompt, OfflineFallback } from "@/components/PWA";
import { useNetworkStatus } from "@/hooks/use-network-status";
import Index from "./pages/Index";
import Profile from "./pages/Profile"; 
import Auth from "./pages/Auth";
import Settings from "./pages/Settings";
import Groups from "./pages/Groups";
import GroupDetails from "./pages/GroupDetails";
import ChannelChat from "./pages/ChannelChat";
import NotFound from "./pages/NotFound";
import CreatePrompt from "./pages/CreatePrompt";
import Prompts from "./pages/Prompts";
import Donate from "./pages/Donate";
import UserProfile from "./pages/UserProfile";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Cookies from "./pages/Cookies";
import RefundPolicy from "./pages/RefundPolicy";
import AuthCallback from "./pages/AuthCallback";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import PromptPage from "./pages/PromptPage";
import Install from "./pages/Install";
import Docs from "./pages/Docs";
import CollectionDetail from "./pages/CollectionDetail";
import { AuthStateManager } from "@/components/AuthStateManager";

const AppContent = () => {
  const { status } = useNetworkStatus();
  
  // Debug flags from localStorage
  const disableOfflineUI = typeof localStorage !== 'undefined' && localStorage.getItem("disable_offline_ui") === "1";
  const showDebugBanner = typeof localStorage !== 'undefined' && localStorage.getItem("debug_network_ui") === "1";
  
  useEffect(() => {
    // Clear any persistent toast messages from localStorage
    localStorage.removeItem('groupResetSuccess');
  }, []);

  // Always log status for debugging
  console.log("[App] Network status:", status, "| navigator.onLine:", navigator.onLine, "| bypass:", disableOfflineUI);

  // NUCLEAR OPTION: If bypass flag is set, never show offline UI
  if (disableOfflineUI) {
    console.log("[App] Offline UI BYPASSED via localStorage flag");
  }

  // Only show offline fallback when we are confidently offline (never during initial "checking")
  // AND bypass flag is not set
  if (status === "offline" && !disableOfflineUI) {
    console.log("[App] SHOWING OFFLINE UI - status is offline");
    return <OfflineFallback status={status} />;
  }

  return (
    <AuthStateManager>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/groups" element={<Groups />} />
        <Route path="/groups/:groupId" element={<GroupDetails />} />
        <Route path="/groups/:groupId/channels/:channelId" element={<ChannelChat />} />
        <Route path="/create-prompt" element={<CreatePrompt />} />
        <Route path="/prompts" element={<Prompts />} />
        <Route path="/prompts/:promptId" element={<PromptPage />} />
        <Route path="/donate" element={<Donate />} />
        <Route path="/users/:userId" element={<UserProfile />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/cookies" element={<Cookies />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/install" element={<Install />} />
        <Route path="/docs" element={<Docs />} />
        <Route path="/collections/:collectionId" element={<CollectionDetail />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      <InstallPrompt />
    </AuthStateManager>
  );
};

const App = () => (
  <ErrorBoundary>
    <HelmetProvider>
      <TooltipProvider>
        <BrowserRouter>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </HelmetProvider>
  </ErrorBoundary>
);

export default App;
